import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "shared/ui/Header";
import logo2 from "assets/images/potentialogo.png";
import {
  TrendingUp,
  Target,
  Star,
  BookOpen,
  Award,
  Sparkles,
  Info,
} from "lucide-react";
import { BASE_URL } from "shared/lib/config";

/* ---------- Scoring scale & thresholds (single source of truth) ---------- */
const SCALE_MAX = 130;
const THRESHOLDS = {
  developingMax: 89,  // < 90
  averageMax: 110,    // 90–110
  aboveMax: 120,      // 111–120
  excellentMax: 130,  // 121–130 (cap)
};

/* ---------- Helpers ---------- */
const clampScore = (score) => Math.max(0, Math.min(Number(score) || 0, SCALE_MAX));
const pct130 = (score) => (clampScore(score) / SCALE_MAX) * 100;

/* boundaries at the *minimum* of each band */
const boundaryPercents = {
  devMin: 0,
  avgMin: pct130(THRESHOLDS.developingMax + 1), // 90
  aboveMin: pct130(THRESHOLDS.averageMax + 1),  // 111
  exMin: pct130(THRESHOLDS.aboveMax + 1),       // 121
  end: 100,
};

/* ---------- Bands & Styling Helpers (Indonesian labels) ---------- */
const bandFor = (score) => {
  const s = clampScore(score);
  if (s > THRESHOLDS.aboveMax)
    return {
      id: "excellent",
      labelId: "Unggul",
      chip: "bg-teal-100 text-teal-800 border-teal-200",
      card: "bg-teal-50 border-teal-200",
      bar: "bg-teal-500",
      text: "text-teal-700",
    };
  if (s >= THRESHOLDS.averageMax + 1)
    return {
      id: "above",
      labelId: "Di atas Rata-rata",
      chip: "bg-green-100 text-green-800 border-green-200",
      card: "bg-green-50 border-green-200",
      bar: "bg-green-500",
      text: "text-green-700",
    };
  if (s >= THRESHOLDS.developingMax + 1)
    return {
      id: "average",
      labelId: "Rata-rata",
      chip: "bg-amber-100 text-amber-900 border-amber-200",
      card: "bg-amber-50 border-amber-200",
      bar: "bg-amber-500",
      text: "text-amber-700",
    };
  return {
    id: "developing",
    labelId: "Sedang Berkembang",
    chip: "bg-orange-100 text-orange-900 border-orange-200",
    card: "bg-orange-50 border-orange-200",
    bar: "bg-orange-600",
    text: "text-orange-700",
  };
};

const englishLevel = (bandId) =>
  ({
    excellent: "Unggul",
    above: "Diatas Rata-rata",
    average: "Rata-rata",
    developing: "Berkembang",
  }[bandId] || "—");

/* ---------- Ability descriptions (tooltip on hover/tap) ---------- */
const ABILITY_DEFS = {
  "Vocabulary (Gc)":
    "Kemampuan untuk memahami dan bernalar menggunakan konsep yang dibingkai dalam kata dan bahasa.",
  "Number Sequence (Gf)":
    "Kemampuan untuk bekerja dengan angka, konsep matematika, dan hubungan kuantitatif.",
  "Complex Working Memory (GMW)":
    "Kemampuan untuk menahan dan memanipulasi informasi secara mental dalam jangka waktu yang singkat.",
  "Visualization":
    "Kemampuan untuk membayangkan dan memanipulasi objek di dalam ruang secara mental.",
  "Visualization (GV)":
    "Kemampuan untuk membayangkan dan memanipulasi objek di dalam ruang secara mental.",
  "Semantic Fluency (Gr)":
    "Kemampuan untuk menghasilkan kata-kata secara efisien dalam kategori makna tertentu.",
  "Memory of Sound Pattern (GA)":
    "Kemampuan untuk mengingat dan mengulang urutan bunyi atau pola suara.",
  "Rate of Test Taking (Gs)":
    "Kecepatan dalam mengerjakan tugas secara akurat dan efisien.",
  "Narration Recall (Gl)":
    "Kemampuan untuk mengingat dan menceritakan kembali cerita atau informasi yang disampaikan secara lisan.",
};
const norm = (s) =>
  String(s || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const ABILITY_DEFS_N = Object.fromEntries(
  Object.entries(ABILITY_DEFS).map(([k, v]) => [norm(k), v])
);
/* ---------- Text parsing helpers for dynamic sections ---------- */
const parseIntroAndBullets = (text) => {
  if (!text || typeof text !== "string") return { intro: "", bullets: [] };
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  const bullets = [];
  const introParts = [];
  let bulletStarted = false;

  for (const line of lines) {
    if (/^[-•–]\s+/.test(line)) {
      bulletStarted = true;
      bullets.push(line.replace(/^[-•–]\s+/, "").trim());
    } else {
      if (bulletStarted) {
        bullets.push(line);
      } else {
        introParts.push(line);
      }
    }
  }
  return { intro: introParts.join(" "), bullets };
};

const parseMinatBakat = (text) => {
  const out = { subjects: [], majors: [] };
  if (!text || typeof text !== "string") return out;

  const lines = text.split(/\r?\n/).map((l) => l.trim());

  let section = null;
  for (let raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    if (/^Subjek Sekolah yang Potensial/i.test(line)) {
      section = "subjects";
      continue;
    }
    if (/^Peminatan atau Jurusan Kuliah yang Relevan/i.test(line)) {
      section = "majors";
      continue;
    }

    if (section) {
      const item = line.replace(/^[-•–]\s*/, "").trim();
      if (item) out[section].push(item);
    }
  }
  return out;
};

/* ============================== Component ============================== */

export default function Report() {
  const navigate = useNavigate();
  const { search, state } = useLocation();
    useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  // identify the target student and navigation context
  const studentIdFromQuery = new URLSearchParams(search).get("studentId");
  const voucherFromQuery = new URLSearchParams(search).get("voucher");
  const targetVoucher = (voucherFromQuery || state?.voucher || "").trim();

  const showBack = Boolean(state?.from) || window.history.length > 1;

  // state
  const [profile, setProfile] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [status, setStatus] = useState("Memuat...");
  const [debug, setDebug] = useState(null);

  /* ---------- auth/storage helpers ---------- */
  const getStore = () => {
    if (sessionStorage.getItem("idToken")) return sessionStorage;
    if (localStorage.getItem("idToken")) return localStorage;
    return null;
  };

  const getAuthState = () => {
    const store = getStore();
    if (!store) return { token: null, profile: null, store: null };
    const token = store.getItem("idToken");
    const cached = JSON.parse(store.getItem("userProfile") || "null");
    return { token, profile: cached, store };
  };

  const hardLogout = () => {
    ["idToken", "userProfile"].forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
    navigate("/");
  };

  const verifyTokenOrLogout = async (token, store) => {
    try {
      const r = await fetch(`${BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("unauthorized");
      const freshProfile = await r.json();
      store.setItem("userProfile", JSON.stringify(freshProfile));
      return freshProfile;
    } catch {
      hardLogout();
      return null;
    }
  };

  const pickAssessmentsEndpoint = (role) => {
    if (["admin", "teacher", "student", "generalUser", "parent"].includes(role)) {
      return `${BASE_URL}/api/assessments`;
    }
    return null;
  };

  /* ---------- Hydrate profile immediately, then verify in background ---------- */
  useEffect(() => {
    const { token, profile: cached, store } = getAuthState();

    // 1) paint header instantly
    if (cached) setProfile(cached);

    // 2) refresh silently
    if (token && store) {
      verifyTokenOrLogout(token, store).then((fresh) => {
        if (fresh) setProfile(fresh);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- Fetch/resolve assessments (preload-friendly) ---------- */
  useEffect(() => {
    const preloaded = state?.preloadedAssessment || null;

    // Always show preloaded immediately (no delay)
    if (preloaded) {
      setAssessments([preloaded]);
      setStatus("");
    }

    const { token, store } = getAuthState();
    if (!token || !store) return hardLogout();

    (async () => {
      const freshProfile = await verifyTokenOrLogout(token, store);
      if (!freshProfile) return;

      // Only fetch from API if we didn't already get preloaded data
      if (preloaded) return;

      const endpoint = pickAssessmentsEndpoint(freshProfile.role);
      try {
        setStatus("Loading…");
        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401 || res.status === 403) return hardLogout();

        const data = await res.json();
        setDebug(data);

        if (!res.ok) {
          setStatus(`Error: ${data?.message || res.status}`);
          setAssessments([]);
          return;
        }

        let rows = Array.isArray(data) ? data : data?.data || [];

        const getRowStudentId = (r) => {
          const sid = typeof r?.studentId === "string" ? r.studentId : r?.studentId?._id;
          return sid ? String(sid) : null;
        };

        if (studentIdFromQuery) {
          const tgt = String(studentIdFromQuery);
          let filtered = rows.filter((r) => getRowStudentId(r) === tgt);
          if (!filtered.length && state?.studentName) {
            const nameNorm = state.studentName.trim().toLowerCase();
            filtered = rows.filter((r) => {
              const n1 = (r?.test_taker_name || "").toLowerCase();
              const n2 = (r?.studentId?.name || "").toLowerCase();
              return n1 === nameNorm || n2 === nameNorm;
            });
          }
          rows = filtered;
        } else if (freshProfile?.role === "student") {
          const pid = String(freshProfile?._id || "");
          const pEmail = (freshProfile?.email || "").toLowerCase();
          const pStuId =
            typeof freshProfile?.studentId === "string"
              ? freshProfile.studentId
              : freshProfile?.studentId?._id;

          let filtered = rows.filter((r) => {
            const sid = getRowStudentId(r);
            const email = (r?.studentId?.email || "").toLowerCase();
            return sid === pid || sid === String(pStuId || "") || (email && email === pEmail);
          });

          if (filtered.length) rows = filtered;
        }

        rows.sort((a, b) => {
          const da =
            (a?.createdAtVendor && new Date(a.createdAtVendor)) ||
            (a?.createdAt && new Date(a.createdAt)) ||
            0;
          const db =
            (b?.createdAtVendor && new Date(b.createdAtVendor)) ||
            (b?.createdAt && new Date(b.createdAt)) ||
            0;
          return db - da;
        });

        setAssessments(rows);
        setStatus(rows.length ? "" : "No assessment data found for this student yet.");
      } catch (e) {
        setStatus(e?.message || "Failed to load");
        setAssessments([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentIdFromQuery, voucherFromQuery]);

  /* ---------- pick the assessment to render (by voucher if provided) ---------- */
  const selectedAssessment = useMemo(() => {
    if (!assessments?.length) return null;
    if (targetVoucher) {
      const hit = assessments.find(
        (r) => (r?.voucher || "").toLowerCase() === targetVoucher.toLowerCase()
      );
      if (hit) return hit;
    }
    return assessments[0]; // fallback: latest
  }, [assessments, targetVoucher]);

  /* ---------- derive fields from the selected assessment ---------- */
  const a = selectedAssessment;
  const viewerIsStudent = (profile?.role || "").toLowerCase() === "student";

  const studentName =
    a?.test_taker_name ??
    a?.studentId?.name ??
    a?.studentId?.email ??
    state?.childName ??
    state?.studentName ??
    (viewerIsStudent ? (profile?.name || profile?.email) : "—");

    const schoolName =
  // 1️⃣ Nama sekolah dari dokumen asesmen (paling diutamakan)
  a?.school_name ||
  a?.school ||
  (a?.schoolId && typeof a.schoolId === "object" ? a.schoolId.name : null) ||
  state?.schoolName ||
  // 2️⃣ fallback: sekolah dari akun siswa (kalau asesmen tidak punya field sendiri)
  a?.studentId?.schoolId?.name ||
  a?.studentId?.classId?.schoolId?.name ||
  // 3️⃣ fallback terakhir: sekolah dari profil viewer
  profile?.school?.name ||
  profile?.schoolName ||
  profile?.school ||
  "";


  /* ---------- Role badge logic (viewer-first for generalUser/parent) ---------- */
  const ROLE_LABELS = {
    student: "Siswa",
    teacher: "Guru",
    admin: "Admin",
    principal: "Kepala Sekolah",
    generaluser: "Peserta Asesmen",
    parent: "Orang Tua",
  };

  const originalRole = (profile?.role || "").toLowerCase();
  const isSpecialViewer = originalRole === "generaluser" || originalRole === "parent";
  const subjectNounForIntro = originalRole === "generaluser" ? "anak" : "siswa";

  // role of the assessment subject; often missing in payload → fallback "student"
  const subjectRoleInDoc = ((a?.studentId && a.studentId.role) || state?.studentRole || "student")
    .toString()
    .toLowerCase();

  // if viewer is generalUser/parent, show THEIR role; else show the subject’s role
  const roleKey = isSpecialViewer ? originalRole : subjectRoleInDoc;

  const subjectRoleLabel =
    ROLE_LABELS[roleKey] ??
    (roleKey ? roleKey.charAt(0).toUpperCase() + roleKey.slice(1) : "Student");

  /* ---------- Demographics ---------- */
  const genderRaw = (a?.gender || "").toString().toLowerCase().trim();
  const isFemale = ["perempuan", "female", "girl"].some((kw) => genderRaw.includes(kw));
  const isMale = ["laki", "male", "boy"].some((kw) => genderRaw.includes(kw));
  const genderVal = isMale ? "Laki-laki" : isFemale ? "Perempuan" : null;

  const ageVal = typeof a?.age === "number" && a.age >= 3 && a.age <= 120 ? a.age : null;

  const classNameResolved =
  // 1) populated object dari backend
  (a?.classId && typeof a.classId === "object" ? a.classId.name : null) ||
  // 2) kalau suatu hari backend kirim `class` langsung
  a?.class?.name ||
  // 3) string nama kelas langsung dari dokumen
  a?.class_name ||
  a?.className ||
  // 4) dari student
  (a?.studentId &&
    typeof a.studentId.classId === "object" &&
    a.studentId.classId?.name) ||
  // 5) fallback terakhir
  state?.className ||
  null;


  const generatedOn =
    (a?.createdAtVendor && String(a.createdAtVendor).slice(0, 10)) ||
    (a?.createdAt && new Date(a.createdAt).toISOString().slice(0, 10)) ||
    "—";

  const scores = useMemo(() => (Array.isArray(a?.scores) ? a.scores : []), [a?.scores]);

  const gca = useMemo(() => {
    if (!scores.length) return null;
    const avg = Math.round(
      scores.reduce((acc, x) => acc + (Number(x.score) || 0), 0) / scores.length
    );
    const band = bandFor(avg);
    return { avg, band };
  }, [scores]);

  const topStrength =
    [...scores].sort((x, y) => (y.score || 0) - (x.score || 0))[0]?.metric || "—";

  /* ---------- flexible back handler ---------- */
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (state?.from) {
      navigate(-1);
    } else {
      navigate("/parents", { replace: true });
    }
  };

  /* ---------- UI ---------- */
  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 text-slate-900"
      style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
    >
      {/* Shared header */}
      <Header
        profile={profile}
        onLogout={hardLogout}
        schoolName={schoolName}
        title="Hasil Asesmen"
        showBack={showBack}
        onBack={handleBack}
      />

      {/* ------------------ Main content ------------------ */}
      <main className="mx-auto max-w-6xl px-4 md:px-5 py-6">
        {/* status */}
        {status && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm md:text-base">
            {status}
          </div>
        )}

        {/* -------- Top header card -------- */}
        <section className="bg-white rounded-2xl shadow-lg p-5 md:p-8 mb-6 md:mb-8 border border-indigo-100">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5 md:mb-6">
            <div className="flex items-center gap-3 md:gap-4 min-w-0">
              <img src={logo2} alt="Potentia" className="h-10 md:h-12 w-auto" />
              <div className="min-w-0">
                <p className="text-indigo-600 font-medium text-sm md:text-base">Laporan Kognitif Anak</p>
                <p className="text-xs md:text-sm text-slate-500 whitespace-normal break-words md:truncate">
                  {`Tinjauan menyeluruh terhadap kemampuan kognitif ${subjectNounForIntro}`}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs md:text-sm text-slate-500">Tanggal Pembuatan</p>
              <p className="font-semibold text-slate-900 text-sm md:text-base">{generatedOn}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:[grid-template-columns:1.4fr_1fr_1.2fr] gap-4 md:gap-6 items-stretch min-w-0">
            {/* Student card */}
            <div
              className={[
                "p-4 md:p-5 rounded-xl border",
                isFemale
                  ? "bg-gradient-to-r from-pink-50 to-rose-50 border-pink-200"
                  : "bg-gradient-to-r from-blue-50 to-indigo-50 border-indigo-200",
              ].join(" ")}
            >
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <h3 className="text-base md:text-lg font-semibold text-slate-900">Pengguna</h3>
                <span className="px-2 py-0.5 md:px-2.5 md:py-1 text-[11px] md:text-xs rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
                  {subjectRoleLabel || "Student"}
                </span>
              </div>
              <p className="text-lg md:text-xl font-bold text-indigo-700 leading-snug truncate">
                {studentName}
              </p>
              {/* Hide Sekolah for generalUser/parent */}
              {schoolName &&
                !["generaluser", "parent"].includes((subjectRoleLabel || "").toLowerCase()) && (
                  <p className="mt-1.5 md:mt-2 text-slate-600 text-sm md:text-base truncate">
                    <span className="font-medium">Sekolah:</span> {schoolName}
                  </p>
                )}

              <div className="mt-2.5 md:mt-3 flex flex-wrap gap-2">
                {genderVal && (
                  <span
                    className={[
                      "inline-flex items-center gap-1.5 px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[11px] md:text-xs font-medium bg-white/80 border",
                      isFemale ? "text-rose-700 border-rose-200" : "text-indigo-700 border-indigo-200",
                    ].join(" ")}
                  >
                    <span className="text-sm leading-none">
                      {genderVal === "Laki-laki" ? "♂" : "♀"}
                    </span>
                    {genderVal}
                  </span>
                )}

                {ageVal !== null && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[11px] md:text-xs font-medium bg-white/80 text-amber-700 border border-amber-200">
                    <span className="text-sm leading-none">🎂</span>
                    {ageVal} tahun
                  </span>
                )}

                {classNameResolved && (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 md:px-2.5 md:py-1 rounded-full text-[11px] md:text-xs font-medium bg-white/80 text-emerald-700 border border-emerald-200">
                    <span className="text-sm leading-none">🏷️</span>
                    Kelas {classNameResolved}
                  </span>
                )}
              </div>
            </div>

            {/* Top strength */}
            <div className="p-4 md:p-5 rounded-xl border bg-gradient-to-r from-sky-50 to-cyan-50 border-sky-200">
              <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2 md:mb-3">
                Kekuatan Utama
              </h3>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-500" />
                <p className="text-base md:text-lg font-bold text-sky-700 truncate italic">{topStrength}</p>
              </div>
              <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-slate-600">
                Kemampuan dengan skor tertinggi dalam asesmen ini.
              </p>
            </div>

            {/* Overall Performance (GCA) */}
            <div className={`p-4 md:p-5 rounded-xl border ${gca ? bandFor(gca.avg).card : "bg-slate-50 border-slate-200"}`}>
              <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-2 md:mb-3 italic">
                General Cognitive Ability (GCA)
              </h3>
              {gca ? (
                <>
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <div className="text-2xl md:text-3xl font-extrabold text-slate-900 tabular-nums">
                      {gca.avg}
                    </div>
                    <span className={`px-2.5 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-semibold border ${gca.band.chip}`}>
                      {englishLevel(gca.band.id)}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200/80 rounded-full h-2.5 md:h-3">
                    <div className={`${gca.band.bar} h-2.5 md:h-3 rounded-full transition-all`} style={{ width: `${pct130(gca.avg)}%` }} />
                  </div>
                </>
              ) : (
                <p className="text-slate-500">—</p>
              )}
            </div>
          </div>
        </section>

        {/* -------- GCA narrative -------- */}
        <section className="bg-white rounded-2xl shadow-lg p-5 md:p-8 mb-6 md:mb-8 border border-indigo-100">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-2.5 md:p-3 rounded-xl">
              <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 italic">
              General Cognitive Ability (GCA)
            </h2>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 md:p-6 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-2 mb-3 md:mb-4">
              <span className="text-lg md:text-2xl font-bold text-indigo-700">
                {gca ? englishLevel(gca.band.id) : "—"}
              </span>
              {gca && <span className="text-slate-600 text-sm md:text-base">({gca.avg})</span>}
            </div>

            <div className="mb-3 md:mb-4">
              <div className="w-full bg-slate-200 rounded-full h-2.5 md:h-3 relative overflow-hidden">
                <div className={`${gca ? gca.band.bar : "bg-slate-400"} h-2.5 md:h-3 rounded-full`} style={{ width: `${gca ? pct130(gca.avg) : 0}%` }} />
                {[boundaryPercents.avgMin, boundaryPercents.aboveMin, boundaryPercents.exMin].map((p, i) => (
                  <div key={i} className="absolute top-0 bottom-0 w-px bg-black" style={{ left: `${p}%` }} />
                ))}
              </div>
              <div className="relative mt-1 h-5 md:h-6 text-[10px] md:text-[11px] text-slate-600">
                <span className="absolute left-0">Berkembang</span>
                <span className="absolute" style={{ left: `${boundaryPercents.avgMin}%`, transform: "translateX(-50%)" }}>
                  Rata-rata
                </span>
                <span className="absolute hidden md:block" style={{ left: `${boundaryPercents.aboveMin}%`, transform: "translateX(-50%)" }}>
                  Atas
                </span>
                <span className="absolute hidden md:block" style={{ left: `${boundaryPercents.exMin}%`, transform: "translateX(-50%)" }}>
                  Unggul
                </span>
                <span className="absolute right-0 md:hidden text-right">Atas  Unggul</span>
              </div>

              <p className="text-slate-700 leading-relaxed text-sm md:text-base text-justify">
                GCA ini merangkum kinerja keseluruhan dari seluruh kemampuan yang diukur.
                Skor yang lebih tinggi menunjukkan kemampuan penalaran umum, daya ingat, dan pemrosesan informasi yang lebih kuat.
              </p>
            </div>
          </div>
        </section>

        {/* -------- Abilities grid -------- */}
        <section className="bg-white rounded-2xl shadow-lg p-5 md:p-8 mb-6 md:mb-8 border border-indigo-100">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-2.5 md:p-3 rounded-xl">
              <Target className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">
              Rincian Kemampuan Kognitif
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {scores.map(({ metric, score, recommendation }) => {
              const band = bandFor(score);
              const tooltip = ABILITY_DEFS_N[norm(metric)];
              return (
                <div
                  key={metric}
                  className={`p-4 md:p-6 rounded-xl border-2 transition-all duration-300 hover:shadow-md ${band.card}`}
                >
                  <div className="flex justify-between items-start mb-2 md:mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base md:text-lg font-semibold text-slate-900 italic">
                        {metric}
                      </h3>

                      {/* Mobile: tap-to-open tooltip (details/summary) */}
                      <details className="relative md:hidden">
                        <summary className="list-none cursor-pointer">
                          <Info className="w-4 h-4 text-slate-500 hover:text-slate-700 inline-block align-middle" aria-label="Ability description" />
                        </summary>
                        <div className="absolute left-1/2 -translate-x-1/2 top-6 z-20 w-64">
                          <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-lg text-justify">
                            {tooltip}
                          </div>
                        </div>
                      </details>

                      {/* Desktop: hover-only tooltip */}
                      <div className="relative hidden md:block group">
                        <Info className="w-4 h-4 text-slate-500 group-hover:text-slate-700 inline-block align-middle" aria-hidden="true" />
                        <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-6 z-20 w-80 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-lg text-justify">
                            {tooltip}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-xl md:text-2xl font-bold text-slate-900 tabular-nums">
                        {Number(score).toFixed(2).replace(/\.00$/, "")}
                      </span>
                      <span className={`block text-[11px] md:text-xs mt-1 font-medium ${band.text}`}>
                        {band.labelId}
                      </span>
                    </div>
                  </div>

                  {/* Consistent bar */}
                  <div className="w-full bg-slate-200 rounded-full h-2 md:h-2.5 mb-1.5 md:mb-2 relative overflow-hidden">
                    <div className={`h-2 md:h-2.5 rounded-full ${band.bar}`} style={{ width: `${pct130(score)}%` }} />
                    {[boundaryPercents.avgMin, boundaryPercents.aboveMin, boundaryPercents.exMin].map((p, i) => (
                      <div key={i} className="absolute top-0 bottom-0 w-px bg-black" style={{ left: `${p}%` }} />
                    ))}
                  </div>

                  <div className="relative h-4 md:h-5 text-[10px] md:text-[11px] text-slate-500 select-none">
                    <span className="absolute left-0 whitespace-nowrap">Berkembang</span>
                    <span className="absolute whitespace-nowrap -translate-x-1/2" style={{ left: `${boundaryPercents.avgMin}%` }}>
                      Rata-rata
                    </span>
                    <span className="absolute hidden xl:block whitespace-nowrap -translate-x-1/2" style={{ left: `${boundaryPercents.aboveMin}%` }}>
                      Atas
                    </span>
                    <span className="absolute hidden xl:block whitespace-nowrap -translate-x-1/2" style={{ left: `${boundaryPercents.exMin}%` }}>
                      Unggul
                    </span>
                    <span className="absolute right-0 xl:hidden text-right whitespace-nowrap">
                      &nbsp;Atas&nbsp;Unggul
                    </span>
                  </div>

                  <p className="text-sm text-slate-700 leading-relaxed mt-2 text-justify">
                    {recommendation || "—"}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* -------- Strengths, Learning Style -------- */}
        <section className="space-y-6 md:space-y-8 mb-6 md:mb-8">
          {/* Key Strengths */}
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-8 border border-indigo-100">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-2.5 md:p-3 rounded-xl">
                <Award className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900">Kekuatan Utama</h2>
            </div>

            {parseIntroAndBullets(a?.strengths).intro && (
              <p className="text-slate-700 leading-relaxed mb-3 md:mb-4 text-sm md:text-base text-justify">
                {parseIntroAndBullets(a?.strengths).intro}
              </p>
            )}
            {parseIntroAndBullets(a?.strengths).bullets.length ? (
              <ul className="space-y-2 text-slate-700 text-sm md:text-base text-justify">
                {parseIntroAndBullets(a?.strengths).bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 aspect-square shrink-0 bg-green-500 rounded-full self-center" />
                    {b}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm md:text-base">—</p>
            )}
          </div>

          {/* Learning Style */}
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-8 border border-indigo-100">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="bg-gradient-to-br from-purple-500 to-violet-600 p-2.5 md:p-3 rounded-xl">
                <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900">Gaya Belajar</h2>
            </div>

            {parseIntroAndBullets(a?.learningStyles).intro && (
              <p className="text-slate-700 leading-relaxed mb-3 md:mb-4 text-sm md:text-base text-justify">
                {parseIntroAndBullets(a?.learningStyles).intro}
              </p>
            )}
            {parseIntroAndBullets(a?.learningStyles).bullets.length ? (
              <ul className="space-y-2 text-slate-700 text-sm md:text-base text-justify">
                {parseIntroAndBullets(a?.learningStyles).bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 aspect-square shrink-0 bg-purple-500 rounded-full self-center" />
                    {b}
                  </li>
                ))}
              </ul>
            ) : (
              !parseIntroAndBullets(a?.learningStyles).intro && <p className="text-slate-500 text-sm md:text-base">—</p>
            )}
          </div>
        </section>

        {/* -------- Subject / Future Majors -------- */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-6 md:mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-8 border border-indigo-100">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="bg-gradient-to-br from-teal-500 to-cyan-600 p-2.5 md:p-3 rounded-xl">
                <BookOpen className="h-5 h-5 md:h-6 md:w-6 text-white" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900">Kemampuan Akademik</h2>
            </div>

            {parseMinatBakat(a?.MinatBakat).subjects.length ? (
              <ul className="space-y-2.5 md:space-y-3 text-slate-700 text-sm md:text-base">
                {parseMinatBakat(a?.MinatBakat).subjects.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 aspect-square shrink-0 bg-teal-500 rounded-full self-center" />
                    {s}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm md:text-base">—</p>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-8 border border-indigo-100">
            <div className="flex items-center gap-3 mb-4 md:mb-6">
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-2.5 md:p-3 rounded-xl">
                <Target className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900">Rekomendasi Bidang Studi</h2>
            </div>

            {parseMinatBakat(a?.MinatBakat).majors.length ? (
              <ul className="space-y-2.5 md:space-y-3 text-slate-700 text-sm md:text-base">
                {parseMinatBakat(a?.MinatBakat).majors.map((m, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-2 aspect-square shrink-0 bg-orange-500 rounded-full self-center" />
                    {m}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-slate-500 text-sm md:text-base">—</p>
            )}
          </div>
        </section>

        {/* -------- Recommended Extracurricular Activities -------- */}
        <section className="bg-white rounded-2xl shadow-lg p-5 md:p-8 mb-6 md:mb-8 border border-indigo-100">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div className="bg-gradient-to-br from-fuchsia-500 to-purple-600 p-2.5 md:p-3 rounded-xl">
              <Sparkles className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <h2 className="text-lg md:text-xl font-bold text-slate-900">
              Rekomendasi Kegiatan Ekstrakurikuler
            </h2>
          </div>

          {parseIntroAndBullets(a?.extracurricularRecommendations).intro && (
            <p className="text-slate-600 mb-4 md:mb-6 text-sm md:text-base text-justify leading-relaxed">
              {parseIntroAndBullets(a?.extracurricularRecommendations).intro}
            </p>
          )}

          {parseIntroAndBullets(a?.extracurricularRecommendations).bullets.length ? (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-slate-700 text-sm md:text-base">
              {parseIntroAndBullets(a?.extracurricularRecommendations).bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-2 aspect-square shrink-0 bg-indigo-500 rounded-full self-center" />
                  <span className="leading-relaxed text-justify">{b}</span>
                </li>
              ))}
            </ul>
          ) : (
            !parseIntroAndBullets(a?.extracurricularRecommendations).intro && (
              <p className="text-slate-500 text-sm md:text-base">—</p>
            )
          )}

          <div className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 md:px-4 py-2.5 md:py-3 text-xs md:text-sm text-indigo-900 text-justify">
            <span className="font-semibold">Catatan: </span>
            Rekomendasi ini didasarkan pada kekuatan kognitif dan profil gaya belajar siswa.
            Partisipasi dalam kegiatan ini dapat membantu mengembangkan kemampuan alaminya
            lebih lanjut, sekaligus membangun rasa percaya diri dan keterampilan sosial.
          </div>
        </section>

        {/* -------- Debug (optional) -------- */}
        {/* <pre className="text-xs text-slate-500">{JSON.stringify(debug, null, 2)}</pre> */}
      </main>

      {/* Floating “Need help?” button */}
      <Link
        to={{ pathname: "/contact", search: "?from=teacher" }}
        state={{ from: "teacher" }}
        onClick={() => sessionStorage.setItem("fromTeacher", "1")}
        className="fixed bottom-6 right-6 md:bottom-10 md:right-10 z-50 flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-full shadow-lg hover:shadow-2xl hover:scale-[1.08] transition-all duration-300 ease-out group"
        title="Perlu bantuan?"
        style={{ marginBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      >
        <svg
          className="w-7 h-7 transition-transform duration-300 group-hover:rotate-12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18h.01M8 10a4 4 0 118 0c0 2-2 3-2 3M12 22a10 10 0 100-20 10 10 0 000 20z"
          />
        </svg>
        <span className="absolute inset-0 rounded-full bg-blue-500 opacity-0 blur-md group-hover:opacity-30 transition-opacity duration-300" />
      </Link>
    </div>
  );
}
